import { z } from 'zod'

import { MeSchema } from '~/@entities/User'

// /login/info 응답. 전화번호로 로그인하는 사용자가 약관에 동의했는지를 알려준다.
export const LoginInfoSchema = z.object({
  isPolicyAgreed: z.boolean(),
})
export type LoginInfo = z.infer<typeof LoginInfoSchema>

// 로그인·재발급 계열 응답(LoginResponse). 사용자 정보는 /users/me 와 같은 모양이고 토큰 둘이 더해진다.
// 서버가 함께 내는 tokenType 은 앱이 읽지 않고, permissions 는 항상 빈 배열이라 받지 않는다.
// restrictions 는 /users/me 와 달리 non-null 선언이고 서버가 filterNotNull 로 채운다.
// 2026-10-02 백엔드 origin/develop 의 LoginResponse.kt 로 확인.
export const LoginSessionSchema = MeSchema.extend({
  restrictions: z.array(z.string()),
  accessToken: z.string(),
  refreshToken: z.string(),
})
export type LoginSession = z.infer<typeof LoginSessionSchema>

/***************** 소속 전환 *****************/
/** 전환할 수 없는 이유. 판정 순서대로 먼저 걸린 하나만 내려온다 */
export const TEACHER_SWITCH_UNAVAILABLE_REASON = [
  /** 학원이 서비스 이용 불가 상태 */
  'ACADEMY_NOT_SERVICEABLE',
  /** 학원이 해당 서비스 미구독 */
  'SERVICE_NOT_SUBSCRIBED',
  /** 학원은 구독했으나 이 선생님에게 접근 권한 없음 */
  'SERVICE_ACCESS_NOT_ALLOWED',
] as const
export const TeacherSwitchUnavailableReasonSchema = z.enum(TEACHER_SWITCH_UNAVAILABLE_REASON)
export type TeacherSwitchUnavailableReason = z.infer<typeof TeacherSwitchUnavailableReasonSchema>

// 같은 사람의 다른 소속 선생님 계정 — 전환 가능 목록의 한 항목.
// 서버가 함께 내는 authority 는 받지 않는다. FE 의 권한 판단은 전환 응답의 authorities 로 다시 산출되므로
// 목록의 값을 신뢰할 이유가 없다.
// unavailableReason 은 서버가 인증 서버 값을 `String?` 로 그대로 넘겨 스펙이 문자열로 그린다. 값 목록은 위 어휘다.
// 2026-10-16 백엔드 develop(b1add2a8c) 의 TeacherSwitchCandidateResponse.kt 로 확인.
export const TeacherSwitchCandidateSchema = z.object({
  teacherId: z.string(),
  academyId: z.string(),
  academyName: z.string(),
  /** 현재 진입해 있는 학원. 목록에 함께 담겨 오며 항상 `switchable: true` 다 */
  current: z.boolean(),
  switchable: z.boolean(),
  /** `switchable === (unavailableReason === null)` — 둘은 항상 짝이다 */
  unavailableReason: TeacherSwitchUnavailableReasonSchema.nullable(),
})
export type TeacherSwitchCandidate = z.infer<typeof TeacherSwitchCandidateSchema>

export const TeacherSwitchCandidatesSchema = z.object({
  candidates: z.array(TeacherSwitchCandidateSchema),
})
export type TeacherSwitchCandidates = z.infer<typeof TeacherSwitchCandidatesSchema>

/***************** 학생 대리 로그인 *****************/
// 선생님 앱이 학생 앱을 대리 로그인 상태로 열 때 쓰는 일회용 토큰
export const StudentImpersonationAuthTokenSchema = z.object({
  studentImpersonationAuthToken: z.string(),
})
export type StudentImpersonationAuthToken = z.infer<typeof StudentImpersonationAuthTokenSchema>
